import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Slider from '@mui/material/Slider';
import SourceLink from './SourceLink';
import { getActionsForTenant, tileSx } from './deviceActions';
import {
  postDeviceControl,
  postDeviceLogcat,
  postDeviceLogList,
  postRemoteScreenWebrtcAddress,
  enableRemoteScreen,
  fetchDeviceInfo,
  postLauncherCustomText,
  postLauncherConfigPush,
  postCleanAppDataConfigPush,
  postPushMessageConfigPush,
} from '../services/api';
import type { DeviceLogListItem } from '../services/api';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import RemoteScreenManager from '../utils/remoteScreenManager';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import type { UnifiedDeviceInfo } from '../types/device';
import { formatEpochLocal } from '../utils/helpers';
import { useNavigate } from 'react-router-dom';

export default function DevicePage({ device, onBack }: { device: UnifiedDeviceInfo; onBack: () => void }) {
  const navigate = useNavigate();
  const [snack, setSnack] = React.useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [loadingAction, setLoadingAction] = React.useState<string | null>(null);
  const [tab, setTab] = React.useState<number>(0);
  const [extraInfoLoading, setExtraInfoLoading] = React.useState<boolean>(false);
  const [extraInfo, setExtraInfo] = React.useState<Record<string, any> | null>(null);
  // keep a local copy of device so we can merge server-returned fields
  const [deviceState, setDeviceState] = React.useState<UnifiedDeviceInfo>(device);
  const [logcatOpen, setLogcatOpen] = React.useState(false);
  const [launcherTextOpen, setLauncherTextOpen] = React.useState(false);
  const [launcherTextValue, setLauncherTextValue] = React.useState<string>('');
  const [pushConfigOpen, setPushConfigOpen] = React.useState(false);
  const [pushConfigType, setPushConfigType] = React.useState<'push-launcher-config' | 'push-clean-app-data' | 'push-message-config' | null>(null);
  const [pushTaskName, setPushTaskName] = React.useState('');
  const [pushSubmitting, setPushSubmitting] = React.useState(false);
  const [preferencesOpen, setPreferencesOpen] = React.useState(false);
  const [prefLanguage, setPrefLanguage] = React.useState<string>('en-US');
  // default to 30 minutes in ms
  const [prefSleepTime, setPrefSleepTime] = React.useState<string>('1800000');
  const [prefAutoResolution, setPrefAutoResolution] = React.useState<boolean>(true);
  const [prefResolution, setPrefResolution] = React.useState<string>('2160p60hz');
  // default to middle of 0-25 range
  const [prefVolume, setPrefVolume] = React.useState<number>(12);
  const [prefSubmitting, setPrefSubmitting] = React.useState(false);
  const [logcatCaptureType, setLogcatCaptureType] = React.useState<string>('Custom');
  const [logcatScope, setLogcatScope] = React.useState<string>('Global');
  const [logcatCaptureRange, setLogcatCaptureRange] = React.useState<string>('All');
  const [logcatLevel, setLogcatLevel] = React.useState<string>('Info');
  const [logcatUploadFileSize, setLogcatUploadFileSize] = React.useState<string>('10MB');
  const [logcatUploadPeriod, setLogcatUploadPeriod] = React.useState<string>('10 Minutes');
  const [logcatMode, setLogcatMode] = React.useState<'once' | 'continuous'>('once');
  const [logcatCaptureDuration, setLogcatCaptureDuration] = React.useState<string>('1 Day');
  const [logcatEvent, setLogcatEvent] = React.useState<string>('Capture logs with HDMI plugged in and out');
  const [logcatPackageNames, setLogcatPackageNames] = React.useState<string>('');
  const [logListLoading, setLogListLoading] = React.useState(false);
  const [logListItems, setLogListItems] = React.useState<DeviceLogListItem[]>([]);
  const [logListPage, setLogListPage] = React.useState(0);
  const [logListPageSize, setLogListPageSize] = React.useState(10);
  const [logListTotal, setLogListTotal] = React.useState(0);
  const handleCopy = React.useCallback(async (label: string, value: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setSnack({ open: true, message: `${label} copied`, severity: 'success' });
    } catch (err) {
      console.error('Copy failed', err);
      setSnack({ open: true, message: `Failed to copy ${label}`, severity: 'error' });
    }
  }, []);

  const handleRunLogcat = React.useCallback(async () => {
    const sn = deviceState?.sn ?? deviceState?.id ?? '';
    if (!sn) {
      setSnack({ open: true, message: 'Device SN is missing', severity: 'error' });
      return;
    }
    if (logcatCaptureType === 'Custom' && logcatScope === 'Specific Application' && !logcatPackageNames.trim()) {
      setSnack({ open: true, message: 'Package names are required for Specific Application scope', severity: 'error' });
      return;
    }
    try {
      const captureRangeMap: Record<string, number> = {
        All: 0,
        Logcat: 1,
        Bugreport: 2,
        ANR: 3,
      };
      const logLevelMap: Record<string, string> = {
        Verbose: 'V',
        Debug: 'D',
        Info: 'I',
        Warn: 'W',
        Error: 'E',
      };
      const uploadFileSizeMap: Record<string, number> = {
        '5MB': 5,
        '10MB': 10,
        '15MB': 15,
      };
      const uploadPeriodMap: Record<string, number | undefined> = {
        '5 Minutes': 5,
        '10 Minutes': 10,
        '15 Minutes': 15,
      };
      const durationDaysMap: Record<string, number> = {
        '1 Day': 1,
        '2 Days': 2,
        '3 Days': 3,
        '5 Days': 5,
        '7 Days': 7,
        '15 Days': 15,
      };
      const durationDays = durationDaysMap[logcatCaptureDuration] ?? 1;
      const stopUploadTime = logcatMode === 'continuous' ? durationDays * 24 * 60 * 60 : undefined;

      const response = await postDeviceLogcat({
        sn,
        logLevel: logLevelMap[logcatLevel] ?? 'I',
        stopUploadTime,
        packageNames: logcatPackageNames.trim() || undefined,
        catchLogType: logcatCaptureType === 'Event' ? 1 : undefined,
        uploadFileSize: uploadFileSizeMap[logcatUploadFileSize] ?? 10,
        uploadPeriod: uploadPeriodMap[logcatUploadPeriod],
        captureRange: captureRangeMap[logcatCaptureRange] ?? 0,
      });
      const logcatCode = response?.code ?? response?.data?.code;
      const logcatMessage = response?.message ?? response?.data?.message;
      const logcatSuccess = logcatCode === 0 || logcatCode === 200;
      setSnack({
        open: true,
        message: logcatMessage ? `Logcat: ${logcatMessage}` : 'Logcat request sent',
        severity: logcatSuccess ? 'success' : 'error',
      });
      if (!logcatSuccess) {
        return;
      }
      setLogcatOpen(false);
    } catch (err: any) {
      console.error('Logcat request failed', err);
      const detail = err?.response?.data?.message ?? err?.message ?? String(err);
      const short = (detail ?? '').toString().slice(0, 200);
      setSnack({ open: true, message: `Failed to request logcat: ${short}`, severity: 'error' });
    }
  }, [deviceState, logcatCaptureDuration, logcatCaptureRange, logcatCaptureType, logcatLevel, logcatMode, logcatScope, logcatUploadFileSize, logcatUploadPeriod, logcatPackageNames]);

  const handleSubmitLauncherText = React.useCallback(async () => {
    const mac = deviceState?.mac ?? '';
    const customText = launcherTextValue.trim();
    if (!mac) {
      setSnack({ open: true, message: 'Device MAC is missing', severity: 'error' });
      return;
    }
    if (!customText) {
      setSnack({ open: true, message: 'Please enter custom text', severity: 'error' });
      return;
    }
    try {
      const response = await postLauncherCustomText({ mac, customText });
      const launcherCode = response?.code ?? response?.data?.code;
      const launcherMessage = response?.message ?? response?.data?.message;
      const launcherSuccess = launcherCode === 0 || launcherCode === 200;
      setSnack({
        open: true,
        message: launcherMessage ? `Launcher text: ${launcherMessage}` : 'Launcher text updated',
        severity: launcherSuccess ? 'success' : 'error',
      });
      if (!launcherSuccess) {
        return;
      }
      setLauncherTextOpen(false);
      setLauncherTextValue('');
    } catch (err: any) {
      console.error('Launcher text update failed', err);
      const detail = err?.response?.data?.message ?? err?.message ?? String(err);
      const short = (detail ?? '').toString().slice(0, 200);
      setSnack({ open: true, message: `Failed to update launcher text: ${short}`, severity: 'error' });
    }
  }, [deviceState, launcherTextValue]);

  const handleOpenPushConfig = React.useCallback((actionType: 'push-launcher-config' | 'push-clean-app-data' | 'push-message-config') => {
    const mac = deviceState?.mac?.trim();
    if (!mac) {
      setSnack({ open: true, message: 'Device MAC is missing', severity: 'error' });
      return;
    }
    setPushConfigType(actionType);
    setPushTaskName('');
    setPushConfigOpen(true);
  }, [deviceState]);

  const handleSubmitPushConfig = React.useCallback(async () => {
    if (!pushConfigType) return;
    const mac = deviceState?.mac?.trim();
    const taskValue = pushTaskName.trim();
    if (!mac) {
      setSnack({ open: true, message: 'Device MAC is missing', severity: 'error' });
      return;
    }
    if (!taskValue) {
      setSnack({ open: true, message: 'Task Name is required', severity: 'error' });
      return;
    }
    setPushSubmitting(true);
    try {
      let response: any;
      let successMessage = '';
      if (pushConfigType === 'push-launcher-config') {
        response = await postLauncherConfigPush({ mac, taskName: taskValue });
        successMessage = 'Launcher config push requested';
      } else if (pushConfigType === 'push-clean-app-data') {
        response = await postCleanAppDataConfigPush({ mac, taskName: taskValue });
        successMessage = 'Clean app data config push requested';
      } else {
        response = await postPushMessageConfigPush({ mac, taskName: taskValue });
        successMessage = 'Push message config requested';
      }
      const pushCode = response?.code ?? response?.data?.code;
      const pushMessage = response?.message ?? response?.data?.message;
      const pushSuccess = pushCode === 0 || pushCode === 200;
      setSnack({
        open: true,
        message: pushMessage ? `Push config: ${pushMessage}` : successMessage,
        severity: pushSuccess ? 'success' : 'error',
      });
      if (!pushSuccess) {
        return;
      }
      setPushConfigOpen(false);
      setPushTaskName('');
    } catch (err: any) {
      const detail = err?.response?.data?.message ?? err?.message ?? String(err);
      const short = (detail ?? '').toString().slice(0, 200);
      setSnack({ open: true, message: `Push config failed: ${short}`, severity: 'error' });
    } finally {
      setPushSubmitting(false);
    }
  }, [deviceState, pushConfigType, pushTaskName]);

  const handleFetchLogList = React.useCallback(async (page: number, pageSize: number) => {
    const sn = deviceState?.sn ?? deviceState?.id ?? '';
    if (!sn) {
      setSnack({ open: true, message: 'Device SN is missing', severity: 'error' });
      return;
    }
    setLogListLoading(true);
    try {
      const res = await postDeviceLogList({
        entity: { sn },
        pageNum: page + 1,
        pageSize,
        orderType: 'desc',
        sortColumns: 'createTime',
      });
      setLogListItems(res?.data?.list ?? []);
      setLogListTotal(res?.data?.total ?? 0);
    } catch (err: any) {
      console.error('Failed to fetch device log list', err);
      const detail = err?.response?.data?.message ?? err?.message ?? String(err);
      const short = (detail ?? '').toString().slice(0, 200);
      setSnack({ open: true, message: `Failed to load logcat list: ${short}`, severity: 'error' });
    } finally {
      setLogListLoading(false);
    }
  }, [deviceState]);

  // Use the stable `device` prop (instead of `deviceState`) so
  // async updates to `deviceState` (from fetchDeviceInfo) don't
  // cause the actions calculation to run multiple times with
  // intermediate values. The `device` prop should represent the
  // canonical device source for the UI.
  const availableActions = React.useMemo(
    () => getActionsForTenant(device?.source),
    [device?.source]
  );

  React.useEffect(() => {
    // reset local state when parent passes a new device
    setDeviceState(device);
  }, [device]);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      if (!device) return;
      setExtraInfoLoading(true);
      try {
        const res = await fetchDeviceInfo(device);
        const payload = res?.data ?? res;
        // backend returns a UnifiedDeviceInfo with extraInfo map and other fields
        if (mounted) {
          setExtraInfo(payload?.extraInfo ?? payload?.extra_info ?? null);
          // merge returned fields into local device state so the UI updates
          setDeviceState((prev) => {
            const merged = { ...prev, ...(payload ?? {}) };
            if ((!merged.groups || merged.groups.length === 0) && device?.groups?.length) {
              merged.groups = device.groups;
            }
            return merged;
          });
        }
      } catch (err) {
        console.error('Failed to fetch extra device info', err);
        if (mounted) setExtraInfo(null);
      } finally {
        if (mounted) setExtraInfoLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [device]);

  React.useEffect(() => {
    if (tab === 2) {
      handleFetchLogList(logListPage, logListPageSize);
    }
  }, [handleFetchLogList, logListPage, logListPageSize, tab]);
  return (
    <Box sx={{ p: 0.5, pl: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
  <Button variant="outlined" startIcon={<ArrowBackIcon />} size="small" onClick={onBack}>Back</Button>
  <Typography variant="h5">Device: {deviceState.sn ?? deviceState.id}</Typography>
      </Box>

  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
  {availableActions.map((a) => (
          <Box key={a.label} sx={{ width: { xs: '48%', sm: '23%', md: '15%' } }}>
            <Button
              fullWidth
              variant="contained"
              sx={(a.command || a.label === 'Logcat' || a.label === 'Launcher Text' || a.actionType) ? tileSx(a.color ?? '#1976d2') : { backgroundColor: '#ddd', color: '#666', height: 56, textTransform: 'none' }}
              startIcon={loadingAction === a.label ? <CircularProgress size={18} color="inherit" /> : <a.Icon />}
              disabled={(!a.command && a.label !== 'Logcat' && a.label !== 'Launcher Text' && !a.actionType) || loadingAction === a.label}
              onClick={async () => {
                if (a.label === 'Logcat') {
                  setLogcatOpen(true);
                  return;
                }
                if (a.label === 'Launcher Text') {
                  setLauncherTextOpen(true);
                  return;
                }
                if (a.actionType) {
                  handleOpenPushConfig(a.actionType);
                  return;
                }
                if (a.label === 'Preferences') {
                  setPreferencesOpen(true);
                  return;
                }
                if (!a.command) return;
                setLoadingAction(a.label);
                try {
                  // Special-case Remote Screen (action without command)
                    if (a.label === 'Remote Screen') {
                    // call the local gateway endpoint to enable remote screen and fetch tokens
                    const sn = deviceState?.sn ?? deviceState?.id ?? '';
                    if (!sn) {
                      throw new Error('Device SN is missing');
                    }
                    if ((deviceState?.source ?? '').toLowerCase() === 'tgmaxx') {
                      const resp = await postRemoteScreenWebrtcAddress({ sn });
                      const remoteCode = resp?.code;
                      const remoteMessage = resp?.message;
                      const remoteSuccess = remoteCode === 0 || remoteCode === 200;
                      if (!remoteSuccess) {
                        setSnack({
                          open: true,
                          message: remoteMessage ? `Remote Screen: ${remoteMessage}` : 'Remote Screen request failed',
                          severity: 'error',
                        });
                        return;
                      }
                      const url = resp?.data;
                      const mac = deviceState?.mac ?? '';
                      setSnack({
                        open: true,
                        message: url ? `Remote Screen URL: ${url}` : 'Remote Screen URL not provided',
                        severity: url ? 'success' : 'error',
                      });
                      if (url) {
                        const macParam = mac ? `&mac=${encodeURIComponent(mac)}` : '';
                        navigate(`/remote-screen?streamUrl=${encodeURIComponent(url)}&sn=${encodeURIComponent(sn)}${macParam}`);
                      }
                    } else {
                      const resp = await enableRemoteScreen(sn);
                      // expected shape: { data: { accessToken, refreshToken } } or similar
                      const data = resp?.data ?? resp;
                      const accessToken = data?.accessToken || data?.token || data?.access_token;
                      const refreshToken = data?.refreshToken || data?.refresh_token;
                      if (!accessToken) throw new Error('No access token in response');

                      const manager = new RemoteScreenManager({
                        sn: device.sn,
                        theme: 'light',
                        language: 'en',
                        tenantConfig: {
                            themeConfig: {
                                primary: '#22B3E8',
                            }
                        },
                        token: accessToken,
                        refreshToken: refreshToken,
                        targetUrl: 'https://www.skywayplatform.com/skyway-micro-modules/skyway-remote-screen.html',
                        // debug: true,
                      });
                      setSnack({ open: true, message: `Opening Remote Screen for ${device.sn}`, severity: 'success' });
                      // open() will open a new window/tab centered per manager implementation
                      manager.open();
                    }
                  } else {
                    const payload = {
                      source: deviceState.source,
                      deviceId: deviceState.sn ?? deviceState.id,
                      deviceCommandEnum: a.command,
                      params: a.label === 'Clean Data' ? { scale: 0 } : undefined,
                    };
                    const resp = await postDeviceControl(payload as any);
                    const topCode = resp?.code;
                    const nestedCode = resp?.data?.code;
                    const responseMessage = resp?.message ?? resp?.data?.message;
                    const isSuccess = topCode === 0 || topCode === 200 || nestedCode === 0 || nestedCode === 200;
                    setSnack({
                      open: true,
                      message: responseMessage ? `${a.label}: ${responseMessage}` : `${a.label} command sent`,
                      severity: isSuccess ? 'success' : 'error',
                    });
                    if (!isSuccess) {
                      return;
                    }
                  }
                } catch (err: any) {
                  console.error('action failed', err);
                  const detail = err?.response?.data?.message ?? err?.message ?? String(err);
                  const short = (detail ?? '').toString().slice(0, 200);
                  setSnack({ open: true, message: `Failed to perform ${a.label}: ${short}`, severity: 'error' });
                } finally {
                  setLoadingAction(null);
                }
              }}
            >
              {a.label}
            </Button>
          </Box>
        ))}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Details" />
          <Tab label="Additional Info" />
          <Tab label="Logcat" />
        </Tabs>
      </Box>

      <Dialog
        open={logcatOpen}
        onClose={() => setLogcatOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#fff' } }}
      >
    <DialogTitle>Logcat</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 1, mt: 1 }}>
            <Box sx={{ px: 1.5, py: 0.75 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="logcat-capture-type-label">Capture Type</InputLabel>
                <Select
                  labelId="logcat-capture-type-label"
                  label="Capture Type"
                  value={logcatCaptureType}
                  onChange={(e) => setLogcatCaptureType(String(e.target.value))}
                  sx={{ bgcolor: 'rgba(240, 242, 245, 0.8)', borderRadius: 1 }}
                  MenuProps={{ PaperProps: { sx: { bgcolor: '#fff' } } }}
                >
                  {['Custom', 'Event'].map((value) => (
                    <MenuItem key={value} value={value}>{value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {logcatCaptureType === 'Event' ? (
              <>
                <Box sx={{ px: 1.5, py: 0.75 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="logcat-event-label">Event</InputLabel>
                    <Select
                      labelId="logcat-event-label"
                      label="Event"
                      value={logcatEvent}
                      onChange={(e) => setLogcatEvent(String(e.target.value))}
                      sx={{ bgcolor: 'rgba(240, 242, 245, 0.8)', borderRadius: 1 }}
                      MenuProps={{ PaperProps: { sx: { bgcolor: '#fff' } } }}
                    >
                      {['Capture logs with HDMI plugged in and out'].map((value) => (
                        <MenuItem key={value} value={value}>{value}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ bgcolor: 'rgba(220, 225, 232, 0.8)', borderRadius: 2, p: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Description</Typography>
                  <Typography variant="body2" color="text.secondary">
                    The Agent APP will simulate a system event and upload the captured logs after the event ends.
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ px: 1.5, py: 0.75 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="logcat-scope-label">Scope</InputLabel>
                    <Select
                      labelId="logcat-scope-label"
                      label="Scope"
                      value={logcatScope}
                      onChange={(e) => setLogcatScope(String(e.target.value))}
                      sx={{ bgcolor: 'rgba(240, 242, 245, 0.8)', borderRadius: 1 }}
                      MenuProps={{ PaperProps: { sx: { bgcolor: '#fff' } } }}
                    >
                      {['Global', 'Specific Application'].map((value) => (
                        <MenuItem key={value} value={value}>{value}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                {logcatScope === 'Specific Application' && (
                  <Box sx={{ px: 1.5, py: 0.75 }}>
                    <TextField
                      label="Package Names"
                      placeholder="Link breaks to separate different package names(com.a.b,com.b.c)"
                      value={logcatPackageNames}
                      onChange={(e) => setLogcatPackageNames(e.target.value)}
                      size="small"
                      required
                      fullWidth
                      sx={{ bgcolor: 'rgba(240, 242, 245, 0.8)', borderRadius: 1 }}
                    />
                  </Box>
                )}
                <Box sx={{ px: 1.5, py: 0.75 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="logcat-capture-range-label">Capture Range</InputLabel>
                    <Select
                      labelId="logcat-capture-range-label"
                      label="Capture Range"
                      value={logcatCaptureRange}
                      onChange={(e) => setLogcatCaptureRange(String(e.target.value))}
                      sx={{ bgcolor: 'rgba(240, 242, 245, 0.8)', borderRadius: 1 }}
                      MenuProps={{ PaperProps: { sx: { bgcolor: '#fff' } } }}
                    >
                      {['All', 'Logcat', 'Bugreport', 'ANR'].map((value) => (
                        <MenuItem key={value} value={value}>{value}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ px: 1.5, py: 0.75 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="logcat-level-label">Log Level</InputLabel>
                    <Select
                      labelId="logcat-level-label"
                      label="Log Level"
                      value={logcatLevel}
                      onChange={(e) => setLogcatLevel(String(e.target.value))}
                      sx={{ bgcolor: 'rgba(240, 242, 245, 0.8)', borderRadius: 1 }}
                      MenuProps={{ PaperProps: { sx: { bgcolor: '#fff' } } }}
                    >
                      {['Verbose', 'Debug', 'Info', 'Warn', 'Error'].map((level) => (
                        <MenuItem key={level} value={level}>{level}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ px: 1.5, py: 0.75 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="logcat-upload-file-size-label">Upload File Size</InputLabel>
                    <Select
                      labelId="logcat-upload-file-size-label"
                      label="Upload File Size"
                      value={logcatUploadFileSize}
                      onChange={(e) => setLogcatUploadFileSize(String(e.target.value))}
                      sx={{ bgcolor: 'rgba(240, 242, 245, 0.8)', borderRadius: 1 }}
                      MenuProps={{ PaperProps: { sx: { bgcolor: '#fff' } } }}
                    >
                      {['5MB', '10MB', '15MB'].map((value) => (
                        <MenuItem key={value} value={value}>{value}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ px: 1.5, py: 0.75 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="logcat-upload-period-label">Upload Period</InputLabel>
                    <Select
                      labelId="logcat-upload-period-label"
                      label="Upload Period"
                      value={logcatUploadPeriod}
                      onChange={(e) => setLogcatUploadPeriod(String(e.target.value))}
                      sx={{ bgcolor: 'rgba(240, 242, 245, 0.8)', borderRadius: 1 }}
                      MenuProps={{ PaperProps: { sx: { bgcolor: '#fff' } } }}
                    >
                      {['5 Minutes', '10 Minutes', '15 Minutes'].map((value) => (
                            <MenuItem key={value} value={value}>{value}</MenuItem>
                          ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ px: 1.5, py: 0.75 }}>
                  <FormControl fullWidth component="fieldset">
                    <RadioGroup
                      value={logcatMode}
                      onChange={(e) => setLogcatMode(e.target.value as 'once' | 'continuous')}
                      sx={{ bgcolor: 'rgba(240, 242, 245, 0.8)', borderRadius: 1, p: 1 }}
                    >
                      <FormControlLabel value="once" control={<Radio size="small" />} label="Capture Once" />
                      <FormControlLabel value="continuous" control={<Radio size="small" />} label="Capture Continuously" />
                    </RadioGroup>
                  </FormControl>
                </Box>
                {logcatMode === 'continuous' && (
                  <Box sx={{ px: 1.5, py: 0.75 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="logcat-capture-duration-label">Capture Duration</InputLabel>
                      <Select
                        labelId="logcat-capture-duration-label"
                        label="Capture Duration"
                        value={logcatCaptureDuration}
                        onChange={(e) => setLogcatCaptureDuration(String(e.target.value))}
                        sx={{ bgcolor: 'rgba(240, 242, 245, 0.8)', borderRadius: 1 }}
                        MenuProps={{ PaperProps: { sx: { bgcolor: '#fff' } } }}
                      >
                        {['1 Day', '2 Days', '3 Days', '5 Days', '7 Days', '15 Days'].map((value) => (
                          <MenuItem key={value} value={value}>Stop after {value.toLowerCase()}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
                <Box sx={{ bgcolor: 'rgba(220, 225, 232, 0.8)', borderRadius: 2, p: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Description</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Collecting 5MB logs will expect to take 10 ~ 15 minutes.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Collecting 10MB logs will expect to take 20 ~ 30 minutes.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Collecting 15MB logs will expect to take 30 ~ 60 minutes.
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogcatOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRunLogcat}>Run</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={launcherTextOpen}
        onClose={() => setLauncherTextOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#fff' } }}
      >
        <DialogTitle>Launcher Text</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 1, mt: 1 }}>
            <Box sx={{ px: 1.5, py: 0.75 }}>
              <TextField
                label="Enter text to display on the device launcher"
                multiline
                minRows={3}
                value={launcherTextValue}
                onChange={(e) => setLauncherTextValue(e.target.value)}
                fullWidth
                sx={{
                  bgcolor: 'rgba(240, 242, 245, 0.8)',
                  borderRadius: 1,
                }}
              />
            </Box>
            <Box sx={{ bgcolor: 'rgba(220, 225, 232, 0.8)', borderRadius: 2, p: 2 }}>
            
              <Typography variant="body2" color="text.secondary">
                This text is displayed on the launcher for the selected device.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLauncherTextOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitLauncherText}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pushConfigOpen}
        onClose={() => {
          if (!pushSubmitting) {
            setPushConfigOpen(false);
            setPushTaskName('');
          }
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#fff' } }}
      >
        <DialogTitle>
          {pushConfigType === 'push-launcher-config'
            ? 'Launcher Config'
            : pushConfigType === 'push-clean-app-data'
              ? 'Clean App Data Config'
              : 'Push Message Config'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <TextField
              label="Device MAC"
              value={deviceState.mac ?? ''}
              size="small"
              disabled
              fullWidth
            />
            <TextField
              label="Task Name"
              value={pushTaskName}
              onChange={(e) => setPushTaskName(e.target.value)}
              placeholder="Task name"
              size="small"
              required
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPushConfigOpen(false)} disabled={pushSubmitting}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitPushConfig} disabled={pushSubmitting}>
            {pushSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preferences dialog for TGStream devices */}
      <Dialog
        open={preferencesOpen}
        onClose={() => { if (!prefSubmitting) setPreferencesOpen(false); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#fff' } }}
      >
        <DialogTitle>Preferences</DialogTitle>
        <DialogContent dividers>
            <Box sx={{ display: 'grid', gap: 3, mt: 2, px: 1 }}>
              {/* Device SN is implicit and sent from deviceState when submitting - not shown */}

              <Box sx={{ px: 1.5, py: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="pref-language-label">Language</InputLabel>
                  <Select
                    labelId="pref-language-label"
                    label="Language"
                    value={prefLanguage}
                    onChange={(e) => setPrefLanguage(String(e.target.value))}
                    sx={{ bgcolor: 'rgba(240, 242, 245, 0.8)', borderRadius: 1 }}
                    MenuProps={{ PaperProps: { sx: { bgcolor: '#fff' } } }}
                  >
                    {/* Full language list with locale codes */}
                      <MenuItem value="af-ZA">Afrikaans</MenuItem>
                      <MenuItem value="am-ET">አማርኛ</MenuItem>
                      <MenuItem value="ar-SA">العربية</MenuItem>
                      <MenuItem value="bg-BG">Български</MenuItem>
                      <MenuItem value="ca-ES">Català</MenuItem>
                      <MenuItem value="cs-CZ">Čeština</MenuItem>
                      <MenuItem value="da-DK">Dansk</MenuItem>
                      <MenuItem value="de-DE">Deutsch</MenuItem>
                      <MenuItem value="el-GR">Ελληνικά</MenuItem>
                      <MenuItem value="en-AU">English (Australia)</MenuItem>
                      <MenuItem value="en-GB">English (United Kingdom)</MenuItem>
                      <MenuItem value="en-IN">English (India)</MenuItem>
                      <MenuItem value="en-US">English (United States)</MenuItem>
                      <MenuItem value="es-ES">Español (España)</MenuItem>
                      <MenuItem value="es-US">Español (Estados Unidos)</MenuItem>
                      <MenuItem value="fa-IR">فارسی</MenuItem>
                      <MenuItem value="fi-FI">Suomi</MenuItem>
                      <MenuItem value="fr-CA">Français (Canada)</MenuItem>
                      <MenuItem value="fr-FR">Français (France)</MenuItem>
                      <MenuItem value="he-IL">עברית</MenuItem>
                      <MenuItem value="hi-IN">हिन्दी</MenuItem>
                      <MenuItem value="hr-HR">Hrvatski</MenuItem>
                      <MenuItem value="hu-HU">Magyar</MenuItem>
                      <MenuItem value="id-ID">Indonesia</MenuItem>
                      <MenuItem value="it-IT">Italiano</MenuItem>
                      <MenuItem value="ja-JP">日本</MenuItem>
                      <MenuItem value="ko-KR">한국어</MenuItem>
                      <MenuItem value="lt-LT">Lietuvių</MenuItem>
                      <MenuItem value="lv-LV">Latviešu</MenuItem>
                      <MenuItem value="ms-MY">Melayu</MenuItem>
                      <MenuItem value="nb-NO">Norsk</MenuItem>
                      <MenuItem value="nl-NL">Nederlands</MenuItem>
                      <MenuItem value="pl-PL">Polski</MenuItem>
                      <MenuItem value="pt-BR">Português (Brasil)</MenuItem>
                      <MenuItem value="pt-PT">Português (Portugal)</MenuItem>
                      <MenuItem value="rm-CH">Rumantsch</MenuItem>
                      <MenuItem value="ro-RO">Română</MenuItem>
                      <MenuItem value="ru-RU">Русский</MenuItem>
                      <MenuItem value="sk-SK">Slovenčina</MenuItem>
                      <MenuItem value="sl-SI">Slovenščina</MenuItem>
                      <MenuItem value="sr-RS">Српски</MenuItem>
                      <MenuItem value="sv-SE">Svenska</MenuItem>
                      <MenuItem value="sw-KE">Kiswahili</MenuItem>
                      <MenuItem value="th-TH">ไทย</MenuItem>
                      <MenuItem value="tr-TR">Türkçe</MenuItem>
                      <MenuItem value="uk-UA">Українська</MenuItem>
                      <MenuItem value="vi-VN">Tiếng</MenuItem>
                      <MenuItem value="zh-CN">中文 (简体)</MenuItem>
                      <MenuItem value="zh-HK">中文 (香港)</MenuItem>
                      <MenuItem value="zh-TW">中文 (繁體)</MenuItem>
                      <MenuItem value="zu-ZA">IsiZulu</MenuItem>
                      <MenuItem value="fil-PH">Filipino</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ px: 1.5, py: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="pref-sleep-label">Sleep Time</InputLabel>
                  <Select
                    labelId="pref-sleep-label"
                    label="Sleep Time"
                    value={prefSleepTime}
                    onChange={(e) => setPrefSleepTime(String(e.target.value))}
                    sx={{ bgcolor: 'rgba(240, 242, 245, 0.8)', borderRadius: 1 }}
                    MenuProps={{ PaperProps: { sx: { bgcolor: '#fff' } } }}
                  >
                    <MenuItem value="900000">15 minutes</MenuItem>
                    <MenuItem value="1200000">20 minutes</MenuItem>
                    <MenuItem value="1800000">30 minutes</MenuItem>
                    <MenuItem value="3600000">1 hour</MenuItem>
                    <MenuItem value="14400000">4 hours</MenuItem>
                    <MenuItem value="28800000">8 hours</MenuItem>
                    <MenuItem value="43200000">12 hours</MenuItem>
                    <MenuItem value="86400000">24 hours</MenuItem>
                    <MenuItem value="-1">Never</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ px: 1.5, py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Auto Resolution</Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={prefAutoResolution}
                        onChange={(_, checked) => setPrefAutoResolution(Boolean(checked))}
                        inputProps={{ 'aria-label': 'auto-resolution' }}
                        size="small"
                      />
                    }
                    label=""
                  />
                </Box>
              </Box>

              {!prefAutoResolution && (
                <Box sx={{ px: 1.5, py: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="pref-resolution-label">Resolution</InputLabel>
                    <Select
                      labelId="pref-resolution-label"
                      label="Resolution"
                      value={prefResolution}
                      onChange={(e) => setPrefResolution(String(e.target.value))}
                      sx={{ bgcolor: 'rgba(240, 242, 245, 0.8)', borderRadius: 1 }}
                      MenuProps={{ PaperProps: { sx: { bgcolor: '#fff' } } }}
                    >
                        <MenuItem value="2160p60hz">4k2k-60hz</MenuItem>
                        <MenuItem value="2160p50hz">4k2k-50hz</MenuItem>
                        <MenuItem value="2160p30hz">4k2k-30hz</MenuItem>
                        <MenuItem value="2160p25hz">4k2k-25hz</MenuItem>
                        <MenuItem value="2160p24hz">4k2k-24hz</MenuItem>
                        <MenuItem value="smpte24hz">4k2k-smpte</MenuItem>
                        <MenuItem value="1080p60hz">1080p-60hz</MenuItem>
                        <MenuItem value="1080p50hz">1080p-50hz</MenuItem>
                        <MenuItem value="1080p24hz">1080p-24hz</MenuItem>
                        <MenuItem value="720p60hz">720p-60hz</MenuItem>
                        <MenuItem value="720p50hz">720p-50hz</MenuItem>
                        <MenuItem value="1080i60hz">1080i-60hz</MenuItem>
                        <MenuItem value="1080i50hz">1080i-50hz</MenuItem>
                        <MenuItem value="576p50hz">576p-50hz</MenuItem>
                        <MenuItem value="480p60hz">480p-60hz</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}

              <Box sx={{ px: 1.5, py: 1 }}>
                <Typography variant="body2" gutterBottom>Volume: {prefVolume}</Typography>
                <Slider
                  value={prefVolume}
                  onChange={(_, v) => setPrefVolume(Array.isArray(v) ? Number(v[0]) : Number(v))}
                  min={0}
                  max={25}
                  step={1}
                />
              </Box>
            </Box>
          </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreferencesOpen(false)} disabled={prefSubmitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const sn = deviceState?.sn ?? deviceState?.id ?? '';
              if (!sn) {
                setSnack({ open: true, message: 'Device SN is missing', severity: 'error' });
                return;
              }
              setPrefSubmitting(true);
              try {
                const payload = {
                  source: deviceState.source,
                  deviceId: sn,
                  deviceCommandEnum: 'SET_PREFERENCE',
                  params: {
                    sleepTime: prefSleepTime,
                    language: prefLanguage,
                    resolution: prefAutoResolution ? 'auto' : prefResolution,
                    volume: prefVolume,
                  }
                };
                const resp = await postDeviceControl(payload as any);
                const code = resp?.code ?? resp?.data?.code;
                const message = resp?.message ?? resp?.data?.message;
                const success = code === 0 || code === 200;
                setSnack({ open: true, message: message ? `Preferences: ${message}` : 'Preferences updated', severity: success ? 'success' : 'error' });
                if (success) setPreferencesOpen(false);
              } catch (err: any) {
                const detail = err?.response?.data?.message ?? err?.message ?? String(err);
                const short = (detail ?? '').toString().slice(0, 200);
                setSnack({ open: true, message: `Failed to set preferences: ${short}`, severity: 'error' });
              } finally {
                setPrefSubmitting(false);
              }
            }}
            disabled={prefSubmitting}
          >
            {prefSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

          {tab !== 1 ? null : (
        <div className="cohesive-surface" style={{ padding: 16, marginBottom: 16 }}>
          <Typography variant="h6" gutterBottom>Additional Info</Typography>
          {extraInfoLoading ? <Typography>Loading...</Typography> : (
            extraInfo && Object.keys(extraInfo).length > 0 ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                {Object.entries(extraInfo).map(([k, v]) => (
                  <React.Fragment key={k}>
                    {/* <Typography component="dt" sx={{ fontWeight: 600 }}>{k}</Typography>
                    <Typography component="dd" sx={{ color: 'text.secondary' }}>{String(v)}</Typography> */}
                    <Typography variant="body2"><strong>{k}</strong></Typography>
                    {k.toLowerCase() === 'esn' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={String(v ?? '')} arrow>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {String(v ?? '').length > 5 ? `${String(v ?? '').slice(0, 5)}…` : String(v ?? '')}
                          </Typography>
                        </Tooltip>
                        <Tooltip title="Copy ESN" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleCopy('ESN', String(v ?? ''))}
                            aria-label="Copy ESN"
                          >
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Typography variant="body2">{String(v)}</Typography>
                    )}
                  </React.Fragment>
                ))}
              </Box>
            ) : <Typography>No extra info available.</Typography>
          )}
        </div>
      )}

      {tab === 2 && (
        <div className="cohesive-surface" style={{ padding: 16 }}>
          <Typography variant="h6" gutterBottom>Logcat</Typography>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Created time (UTC)</TableCell>
                  <TableCell>S No</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logListLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">Loading...</TableCell>
                  </TableRow>
                ) : logListItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">No logcat entries found.</TableCell>
                  </TableRow>
                ) : (
                  logListItems.map((item, index) => (
                    <TableRow key={item.id ?? item.fileId ?? `${item.sn ?? 'log'}-${index}`}>
                      <TableCell>
                        {item.createTime ? new Date(item.createTime).toISOString() : '—'}
                      </TableCell>
                      <TableCell>{item.sn ?? '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.uploadStatus === 2 ? 'Success' : 'Warning'}
                          color={item.uploadStatus === 2 ? 'success' : 'warning'}
                          variant="filled"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {item.downloadUrl ? (
                          <Button
                            size="small"
                            variant="outlined"
                            href={item.downloadUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Download
                          </Button>
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={logListTotal}
            page={logListPage}
            onPageChange={(_, newPage) => setLogListPage(newPage)}
            rowsPerPage={logListPageSize}
            onRowsPerPageChange={(e) => {
              const nextSize = Number(e.target.value);
              setLogListPageSize(nextSize);
              setLogListPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20, 50]}
          />
        </div>
      )}

      {tab === 0 && (
          <div className="cohesive-surface" style={{ padding: 16 }}>
        <Typography variant="h6" gutterBottom>Details</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
          {/* <Box > */}
            {/* <Typography variant="body2"><strong>ID:</strong> {deviceState.id}</Typography> */}
            <Typography variant="body2"><strong>SN:</strong> {deviceState.sn}</Typography>
            <Typography variant="body2"><strong>MAC:</strong> {deviceState.mac}</Typography>
            <Typography variant="body2"><strong>Wireless MAC:</strong> {deviceState.wirelessMac}</Typography>
            <Typography variant="body2"><strong>Manufacturer:</strong> {deviceState.manufacturer}</Typography>
            <Typography variant="body2"><strong>Model:</strong> {deviceState.model}</Typography>
            <Typography variant="body2"><strong>Product:</strong> {deviceState.product}</Typography>
          {/* </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: 1 }}> */}
            <Typography variant="body2"><strong>Software:</strong> {deviceState.software}</Typography>
            <Typography variant="body2"><strong>City:</strong> {deviceState.city}</Typography>
            {deviceState.ip && (
              <Typography variant="body2"><strong>Public IP:</strong> {deviceState.ip}</Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2"><strong>Online:</strong></Typography>
              <span className={`app-status-bubble ${deviceState.online ? 'status-green' : 'status-grey'}`} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2"><strong>Activated:</strong></Typography>
              <span className={`app-status-bubble ${deviceState.activate ? 'status-green' : 'status-grey'}`} />
            </Box>
            <Typography variant="body2"><strong>Source:</strong> <SourceLink source={device.source} /></Typography>
            <Typography variant="body2"><strong>Groups:</strong> {deviceState.groups && deviceState.groups.length > 0 ? deviceState.groups.join(', ') : '\u2014'}</Typography>
            <Typography variant="body2"><strong>Lock Status:</strong> {deviceState.locked === undefined ? '\u2014' : (deviceState.locked ? 'Locked' : 'Unlocked')}</Typography>
            <Typography variant="body2"><strong>Last Online:</strong> {formatEpochLocal(deviceState.lastOnlineTime as any)}</Typography>
            <Typography variant="body2"><strong>Activated Time:</strong> {formatEpochLocal(deviceState.activeTime as any)}</Typography>
          {/* </Box> */}

          {/* <Box>
            <Typography variant="body2"><strong>Manufacturer:</strong> {device.manufacturer}</Typography>
            <Typography variant="body2"><strong>Model:</strong> {device.model}</Typography>
            <Typography variant="body2"><strong>City:</strong> {device.city}</Typography>
            <Typography variant="body2"><strong>Source:</strong> {device.source}</Typography>
          </Box> */}
        </Box>
  </div>
  )}
        <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: '100%' }}>
            {snack.message}
          </Alert>
        </Snackbar>
    </Box>
  );
}